import React, { LegacyRef, useContext, useState } from 'react';
import t from 'react-translate';
import { css } from '@emotion/core';
import { useDispatch, useSelector } from 'react-redux';

// redux
import { wrapThunkAction } from 'redux/utils';
import { deleteTableRow } from 'redux/actions/quizzes';
import { getFlatCourseAliases } from 'redux/selectors/course';
import { ComponentType } from 'redux/schemas/models/lecture-component';

import NvTooltip from 'shared/components/nv-tooltip';
import QuestionSetContext, { SavingIndicator } from 'lecture_pages/services/question-set-context';
import NvIcon from 'shared/components/nv-icon';
import { isRtl } from 'styles/global_defaults/media-queries';
import { halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { gray4, gray6, primary, white } from 'styles/global_defaults/colors';
import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';

type DeleteRowProps = {
  deleteRowRef: LegacyRef<HTMLButtonElement>
  setShowButtons: Function;
  childQuestions: number[][];
  setChildQuestions: Function;
  getTableArray: Function;
  rowIndex: number;
  isDeleteRowDisabled: boolean;
  setIsDeleteRowDisabled: Function;
};
const DeleteRowButton = (props: DeleteRowProps) => {
  const {
    deleteRowRef,
    setShowButtons,
    childQuestions,
    setChildQuestions,
    getTableArray,
    rowIndex,
    isDeleteRowDisabled,
    setIsDeleteRowDisabled,
  } = props;
  const {
    currentQuestion,
    savingIndicatorTimeoutRef,
    setSavingStatus,
    canMakeStructuralChanges,
  } = useContext(QuestionSetContext);
  const { numRows, numColumns, childrenQuestions } = currentQuestion;
  const isMinRowLimitReached = childQuestions.length === 1;
  const isButtonDisabled = isDeleteRowDisabled
    || isMinRowLimitReached
    || !canMakeStructuralChanges;

  const dispatch = useDispatch();
  const courseAliases = useSelector(getFlatCourseAliases);
  const { dataQaQuestionSetAttributes, isCurrentActivityType } = useQuestionSetHelpers();

  const [deleteRowHovered, setDeleteRowHovered] = useState(false);

  const buttonStyles = css`
    &.delete-row-button {
      position: absolute;
      left: 0%;
      transform: ${isRtl() ? 'translateX(calc(100% + 5px))' : 'translateX(calc(-100% - 5px))'};
      border-radius: ${halfSpacing}px;
      width: ${halfSpacing}px;
      background-color: ${isButtonDisabled ? gray6 : `${primary}33`};
      border: none;
      color: ${(isButtonDisabled) ? gray4 : primary};
      .dash-icon {
        transform: rotate(90deg);
      }
    }
    &.delete-row-button:hover {
      background-color: ${isButtonDisabled ? gray6 : primary};
      width: ${isButtonDisabled ? halfSpacing : standardSpacing}px;
      color: ${isButtonDisabled ? gray4 : white};
    }
  `;

  const handleDeleteRow = () => {
    const newQuestions = childQuestions;
    const questions = newQuestions.splice(rowIndex, 1);
    const deletedChildQuestions = [...questions[0]];
    setChildQuestions([...newQuestions]);
    setShowButtons(false);
    setDeleteRowHovered(false);

    setIsDeleteRowDisabled(true);
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);
    wrapThunkAction(dispatch(deleteTableRow(
      {
        questionId: currentQuestion.id,
        rowIndex,
        deletedChildQuestions,
      },
    ))).then(() => {
      setSavingStatus(SavingIndicator.SUCCESS_STATUS);
    }).catch(() => {
      setSavingStatus(SavingIndicator.ERROR_STATUS);
      setChildQuestions(getTableArray(numRows, numColumns, childrenQuestions));
    }).finally(() => {
      setIsDeleteRowDisabled(false);
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  };

  const getTooltipText = () => {
    if (!canMakeStructuralChanges) {
      if (isCurrentActivityType(ComponentType.SURVEY)) {
        return t.SURVEYS.TABLE_QUESTION.DISABLED_TOOLTIP(courseAliases);
      }
      return t.QUIZZES.TABLE_QUESTION.DISABLED_TOOLTIP(courseAliases);
    }
    if (isMinRowLimitReached) {
      return t.QUIZZES.TABLE_QUESTION.MIN_ROW_REACHED();
    }

    return t.QUIZZES.TABLE_QUESTION.DELETE_ROW();
  };

  return (
    <NvTooltip
      text={getTooltipText()}
    >
      <button
        css={buttonStyles}
        type='button'
        onMouseEnter={() => setDeleteRowHovered(true)}
        onMouseLeave={() => setDeleteRowHovered(false)}
        onClick={handleDeleteRow}
        disabled={isButtonDisabled}
        ref={deleteRowRef}
        className='delete-row-button d-flex align-items-center justify-content-center'
        data-qa={dataQaQuestionSetAttributes.questionModal.tableQuestion.deleteRow}
      >
        <NvIcon
          className={`${(!deleteRowHovered || isMinRowLimitReached) ? 'dash-icon' : ''}`}
          size='xs-smallest'
          icon={`${(deleteRowHovered && !isMinRowLimitReached) ? 'trash' : 'dash'}`}
        />
      </button>
    </NvTooltip>
  );
};

export default DeleteRowButton;
